import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

export type Apps = 'channels' | 'boards';
type AppSwitchContextType = {
  currentApp: Apps;
  setCurrentApp(_app: Apps): void;
};

const initialState: AppSwitchContextType = {
  currentApp: 'channels',
  setCurrentApp() {},
};

export const AppSwitchContext = createContext(initialState);

export function useAppSwitchContext() {
  const context = useContext(AppSwitchContext);

  if (!context)
    throw new Error(
      'useAppSwitchContext must be use inside AppSwitchContextProvider',
    );

  return context;
}

type Props = {
  children: React.ReactNode;
};

export function AppSwitchContextProvider({ children }: Props) {
  const [currentApp, setCurrentApp] = useState<Apps>('channels');
  const location = useLocation();

  useEffect(() => {
    if (
      location.pathname.includes('/docs') ||
      location.pathname.includes('/todo') ||
      location.pathname.includes('/boards')
    ) {
      setCurrentApp('boards');
    } else {
      setCurrentApp('channels');
    }
  }, [location.pathname]);

  const memoizedValue = useMemo(
    () => ({
      currentApp,
      setCurrentApp,
    }),
    [currentApp],
  );

  return (
    <AppSwitchContext.Provider value={memoizedValue}>
      {children}
    </AppSwitchContext.Provider>
  );
}
